import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { COOKIE_SESSION } from "../api/types.js";
import type { Services, SessionGrant, SessionUser } from "../api/types.js";
import { RealtimeClient } from "../realtime/client.js";
import { AppStore } from "./store.js";

const TOKEN_KEY = "uz.session";

function readToken(): string | null {
  try {
    return globalThis.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token) globalThis.sessionStorage.setItem(TOKEN_KEY, token);
    else globalThis.sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Without storage the user simply signs in again after a reload.
  }
}

export interface SessionValue {
  services: Services;
  /** False until the stored session (if any) has been checked. */
  ready: boolean;
  user: SessionUser | null;
  store: AppStore;
  client: RealtimeClient | null;
  signIn: (grant: SessionGrant) => void;
  signOut: () => Promise<void>;
  /** Re-reads the signed-in user (after a forced password change). */
  refreshUser: () => Promise<void>;
  /** "reconnecting" while the boot session check is being retried; "failed" once it gave up. */
  connection: ConnectionState;
  retryConnection: () => void;
  /**
   * True once, after a signed-in session really ended (the server said 401 to a fresh check):
   * the login screen shows "your session ended" instead of a silent bounce. Cleared by `signIn`.
   */
  sessionEnded: boolean;
}

export type ConnectionState = "ok" | "reconnecting" | "failed";

/** Waits before each retry of the boot session check (so up to three retries). */
export const DEFAULT_RETRY_DELAYS_MS: readonly number[] = [1000, 2000, 5000];

export const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside <SessionProvider>");
  return value;
}

export function SessionProvider({
  services,
  children,
  retryDelaysMs = DEFAULT_RETRY_DELAYS_MS,
}: {
  services: Services;
  children: ReactNode;
  retryDelaysMs?: readonly number[];
}) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [connection, setConnection] = useState<ConnectionState>("ok");
  const [attempt, setAttempt] = useState(0);
  const [sessionEnded, setSessionEnded] = useState(false);
  const store = useMemo(() => new AppStore(), [token]);
  const signedInRef = useRef(false);

  const clear = useCallback(() => {
    // Only a session that existed can "end"; a first visit just shows the sign-in form.
    if (signedInRef.current) setSessionEnded(true);
    signedInRef.current = false;
    writeToken(null);
    services.api.setToken(null);
    setToken(null);
    setUser(null);
  }, [services]);
  const clearRef = useRef(clear);
  clearRef.current = clear;

  const delaysRef = useRef(retryDelaysMs);
  delaysRef.current = retryDelaysMs;

  useEffect(() => {
    // The session is the HttpOnly cookie, which outlives tabs, reloads and server restarts:
    // always ask the server, whatever this tab remembers (sessionStorage dies with the tab).
    const stored = readToken();
    if (stored) {
      services.api.setToken(stored);
      signedInRef.current = true;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const check = (retry: number) => {
      services.api
        .getMe()
        .then((me) => {
          if (cancelled) return;
          if (me) {
            const next = stored ?? COOKIE_SESSION;
            writeToken(next);
            signedInRef.current = true;
            setToken(next);
            setUser(me);
          } else {
            clearRef.current(); // 401 / 403 / explicit unauthenticated: the only way out
          }
          setConnection("ok");
          setReady(true);
        })
        .catch(() => {
          if (cancelled) return;
          // Transient (5xx, network, gateway restarting): keep the session and try again.
          const delay = delaysRef.current[retry];
          if (delay === undefined) {
            setConnection("failed");
            return;
          }
          setConnection("reconnecting");
          timer = setTimeout(() => check(retry + 1), delay);
        });
    };
    setConnection("ok");
    check(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [services, attempt]);

  const retryConnection = useCallback(() => setAttempt((n) => n + 1), []);

  // A session that must still change its password gets no realtime connection (the gateway refuses it).
  const mustChange = user?.mustChangePassword === true;
  const client = useMemo(
    () =>
      token && !mustChange
        ? new RealtimeClient({
            connect: () => services.connect(),
            token,
            store,
            onUnauthenticated: () => clearRef.current(),
            // A socket rejection is only a hint. Ask the server with a fresh /me: only an explicit
            // 401 ends the session; a 5xx or a dropped network keeps it and the socket retries.
            confirmEnded: async () => {
              try {
                return (await services.api.getMe()) === null;
              } catch {
                return false;
              }
            },
          })
        : null,
    [services, token, store, mustChange],
  );
  useEffect(() => {
    client?.start();
    return () => client?.stop();
  }, [client]);

  // Chat history survives reloads: the server's task list seeds the store.
  useEffect(() => {
    if (!token || mustChange) return;
    let cancelled = false;
    services.api
      .listTasks()
      .then((tasks) => {
        if (!cancelled) store.loadHistory(tasks);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [services, token, store, mustChange]);

  const signIn = useCallback(
    (grant: SessionGrant) => {
      signedInRef.current = true;
      setSessionEnded(false);
      writeToken(grant.token);
      services.api.setToken(grant.token);
      setToken(grant.token);
      setUser(grant.user);
    },
    [services],
  );

  // Signing out is the one deliberate exit: no "session ended" notice afterwards.
  const signOut = useCallback(async () => {
    signedInRef.current = false;
    try {
      await services.api.logout();
    } finally {
      clear();
    }
  }, [services, clear]);

  // Coming back to a tab that sat idle: check once so an expired session shows the sign-in
  // screen with a message instead of failing the next click. Transient errors are ignored.
  useEffect(() => {
    if (!token) return;
    let last = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== "visible" || Date.now() - last < 5 * 60_000) return;
      last = Date.now();
      services.api.getMe().then(
        (me) => {
          if (!me) clearRef.current();
        },
        () => undefined,
      );
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [services, token]);

  const refreshUser = useCallback(async () => {
    const me = await services.api.getMe();
    if (me) setUser(me);
    else clear();
  }, [services, clear]);

  const value = useMemo<SessionValue>(
    () => ({
      services,
      ready,
      user,
      store,
      client,
      signIn,
      signOut,
      refreshUser,
      connection,
      retryConnection,
      sessionEnded,
    }),
    [
      services,
      ready,
      user,
      store,
      client,
      signIn,
      signOut,
      refreshUser,
      connection,
      retryConnection,
      sessionEnded,
    ],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
